import { Injectable } from '@angular/core';
import { catchErrorTypedAsync } from '../../functions-helper.service';
import { WorkspaceAssetStateManagerService } from '../state-management/associations/workspace-asset-state-manager.service';
import { BaseActionService } from '../../../models/base-action-service';
import { IMoveAssets, IRenameAsset } from '../../http-models/asset/asset-http-request-model';
import { WorkspaceAssetApiManagerService } from '../api/managers/associations/workspace-asset-api-manager.service';
import { SingleOrArray } from '../../../models/defines';
import { UploaderService } from '../../../components/file-drop/uploader.service';
import { IUploader, UPLOADER_SERVICE_UNIQUE_IDS_ENUM } from '../../../constants/shared/uploader-service-unique-ids';
import { assetConverter } from '../../../models/assets/assets-converter';

@Injectable()
export class WorkspaceAssetActionService extends BaseActionService implements IUploader {
    constructor(
        private workspaceAssetStateManager: WorkspaceAssetStateManagerService,
        private workspaceAssetApi: WorkspaceAssetApiManagerService,
        private uploaderService: UploaderService
    ) {
        super();
    }

    public readonly type = UPLOADER_SERVICE_UNIQUE_IDS_ENUM.ASSET;
    async removeAssetAsync(assetIds: SingleOrArray<string>, assetName: string) {
        this.setLoadingAction(true);

        const { data, error } = await catchErrorTypedAsync(this.workspaceAssetApi.removeAssetsFromWsAsync(assetIds));
        if (error) {
            this._errorActionSubject.next(`Could not delete asset`);
            return;
        }
        this.onActionSuccess(`🗑️ "${assetName}" Deleted"!`);
        this.setLoadingAction(false);
        if (error) {
            this._errorActionSubject.next(`An error occurred when trying to delete project`);
            return;
        }
    }

    async getAssetsByIdsAsync(assetIds: SingleOrArray<string>) {
        const { data, error } = await this.workspaceAssetApi.getAssetsByIdsAsync(assetIds);
        if (error) {
            this.onActionFailed(`Could not get generated asset. Please try again layer`);
            return null;
        }
        const localAssets = assetConverter.convertArrayOfInAssetsToLocal(data);
        return localAssets;
    }

    async renameFileAsync(body: IRenameAsset, oldName: string) {
        const { data, error } = await this.workspaceAssetApi.renameFileAssetAsync(body);
        if (error) {
            this.onActionFailed(`An error occurred when changing file name ${oldName}`);
            return;
        }
        this.onActionSuccess(`👌 "${oldName}" Renamed to "${body.newName}"!`);
    }

    async moveFileAsync(moveAssetPayload: IMoveAssets, displayName: string, fromFolder: string) {
        const { data, error } = await this.workspaceAssetApi.moveAssetsToFolderAsync(moveAssetPayload);
        if (error) {
            this.onActionFailed(
                `An error occurred when moving file ${displayName} from ${fromFolder} to ${moveAssetPayload.toFolder}`
            );
            return;
        }
        this.onActionSuccess(`✌ "${displayName}" Moved to ${moveAssetPayload.toFolder}!`);
    }

    async uploadAssetAsync(formData: FormData, folder: string) {
        return await this.uploaderService.uploadFileAsync(
            this.type,
            this.workspaceAssetApi.uploadAssetAsync(formData, folder)
        );
    }

    async createFolderAsync(folderName: string) {
        const { data, error } = await this.workspaceAssetApi.createFolderAsync(folderName);
        if (error) {
            this.onActionFailed(`An error occurred when creating ${folderName}`);
            return;
        }
        this.onActionSuccess(`👌 "${folderName}" Created!`);
    }

    // async renameFolderAsync(oldName: string, body: IRenameAsset) {
    //     const { data, error } = await this.workspaceAssetApi.renameFolder(body);
    //     if (error) {
    //         this.onActionFailed(`An error occurred when changing folder ${oldName} name `);
    //         return;
    //     }
    //     this.onActionSuccess(`👌 "${oldName}" Renamed to "${body.newName}"!`);
    // }

    private onActionSuccess(message: string) {
        this._successMessageSubject.next(message);
        this.workspaceAssetStateManager.loadAssetsAsync();
    }

    private onActionFailed(message: string) {
        this._errorActionSubject.next(message);
    }

    private setLoadingAction(isLoading: boolean) {
        if (typeof isLoading !== 'boolean') {
            return;
        }
        this._isLoadingActionSubject.next(isLoading);
    }
}
